/**
 * Inline section graphics , pure SVG, no JS, SSG-compatible.
 * Used as MDX components: <SectionGraphic id="..." />
 */

// ─── shared helpers ────────────────────────────────────────────────────────

const BG   = "hsl(210,30%,5%)";
const SURF = "hsl(210,30%,7%)";
const TEAL = "hsl(179,80%,52%)";
const TEAL2 = "hsl(179,80%,36%)";
const DIM  = "hsl(205,18%,50%)";
const DIM2 = "hsl(205,18%,38%)";
const FG   = "hsl(200,30%,90%)";
const RED  = "hsl(0,60%,60%)";
const RED2 = "hsl(0,60%,45%)";
const BORDER = "hsl(200,30%,94%,0.07)";

// SVG icon components (16×16 path icons, centered at transform origin)
function IconBriefcase({ color }: { color: string }) {
  return (
    <g>
      <path d="M5 6V4.5A1.5 1.5 0 016.5 3h3A1.5 1.5 0 0111 4.5V6" fill="none" stroke={color} strokeWidth="1.3" strokeLinecap="round"/>
      <rect x="2" y="6" width="12" height="9" rx="1.5" fill="none" stroke={color} strokeWidth="1.3"/>
      <line x1="2" y1="10" x2="14" y2="10" stroke={color} strokeWidth="1"/>
    </g>
  );
}

function IconWrench({ color }: { color: string }) {
  return (
    <g>
      <path d="M12 2a4 4 0 00-3.8 5.1L2 13.3l.7.7 6.2-6.2A4 4 0 1012 2z" fill="none" stroke={color} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx="12" cy="4" r="1.2" fill={color}/>
    </g>
  );
}

function IconTrend({ color }: { color: string }) {
  return (
    <g>
      <polyline points="2,13 5.5,8.5 8.5,10.5 13.5,3.5" fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
      <polyline points="10.5,3.5 13.5,3.5 13.5,6.5" fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
    </g>
  );
}

function IconShield({ color }: { color: string }) {
  return (
    <g>
      <path d="M8 1.5L2 4v4.2c0 4 2.5 7.2 6 8.3 3.5-1.1 6-4.3 6-8.3V4L8 1.5z" fill="none" stroke={color} strokeWidth="1.3" strokeLinejoin="round"/>
      <polyline points="5.5,8 7,9.5 10.5,6" fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
    </g>
  );
}

function IconCode({ color }: { color: string }) {
  return (
    <g>
      <polyline points="5.5,4.5 2,8 5.5,11.5" fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
      <polyline points="10.5,4.5 14,8 10.5,11.5" fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
    </g>
  );
}

function IconUnlock({ color }: { color: string }) {
  return (
    <g>
      <rect x="2.5" y="7.5" width="11" height="7.5" rx="1.5" fill="none" stroke={color} strokeWidth="1.3"/>
      <path d="M5.5 7.5V5.5a2.5 2.5 0 015 0" fill="none" stroke={color} strokeWidth="1.3" strokeDasharray="2,1.5" strokeLinecap="round"/>
    </g>
  );
}

function IconCalendar({ color }: { color: string }) {
  return (
    <g>
      <rect x="1.5" y="3" width="13" height="12" rx="1.5" fill="none" stroke={color} strokeWidth="1.3"/>
      <line x1="1.5" y1="7" x2="14.5" y2="7" stroke={color} strokeWidth="1.2"/>
      <line x1="5" y1="1.5" x2="5" y2="4.5" stroke={color} strokeWidth="1.4" strokeLinecap="round"/>
      <line x1="11" y1="1.5" x2="11" y2="4.5" stroke={color} strokeWidth="1.4" strokeLinecap="round"/>
      <polyline points="5,10.5 7,12.5 11,9" fill="none" stroke={color} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
    </g>
  );
}

// ─── graphics ───────────────────────────────────────────────────────────────

function ConversionFunnel() {
  // "Sito generico" vs "Sito su misura" , business impact for WP creation landing
  return (
    <svg viewBox="0 0 340 200" overflow="hidden" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto" aria-hidden="true">
      <defs>
        <clipPath id="sg-cf-c"><rect width="340" height="200" rx="12"/></clipPath>
        <linearGradient id="sg-cf-brand" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={TEAL2}/>
          <stop offset="100%" stopColor={TEAL}/>
        </linearGradient>
        <style>{`
          @keyframes sg-cf-up{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
          .sg-cf-1{animation:sg-cf-up .4s ease-out .1s both}
          .sg-cf-2{animation:sg-cf-up .4s ease-out .3s both}
          .sg-cf-3{animation:sg-cf-up .4s ease-out .5s both}
        `}</style>
      </defs>
      <g clipPath="url(#sg-cf-c)">
        <rect width="340" height="200" rx="12" fill={BG}/>

        {/* LEFT , generic site */}
        <g className="sg-cf-1">
          <rect x="10" y="14" width="148" height="172" rx="9" fill={SURF} stroke="hsl(0,60%,50%,0.18)" strokeWidth="1"/>
          <rect x="10" y="14" width="148" height="24" rx="9" fill="hsl(0,55%,15%,0.5)"/>
          <rect x="10" y="26" width="148" height="12" fill="hsl(0,55%,15%,0.5)"/>
          <text x="84" y="31" textAnchor="middle" fill={RED} fontSize="8" fontFamily="JetBrains Mono, monospace" letterSpacing="1.5">SITO GENERICO</text>

          {/* metric 1 */}
          <text x="84" y="65" textAnchor="middle" fill={RED} fontSize="30" fontWeight="700" fontFamily="Space Grotesk, sans-serif">p.8+</text>
          <text x="84" y="80" textAnchor="middle" fill={RED2} fontSize="8" fontFamily="JetBrains Mono, monospace">posizione media Google</text>

          <line x1="26" y1="96" x2="142" y2="96" stroke="hsl(210,30%,11%)" strokeWidth="1"/>

          {/* metric 2 */}
          <text x="84" y="125" textAnchor="middle" fill={RED} fontSize="30" fontWeight="700" fontFamily="Space Grotesk, sans-serif">1%</text>
          <text x="84" y="140" textAnchor="middle" fill={RED2} fontSize="8" fontFamily="JetBrains Mono, monospace">tasso contatti visitatori</text>

          <line x1="26" y1="156" x2="142" y2="156" stroke="hsl(210,30%,11%)" strokeWidth="1"/>

          <text x="84" y="173" textAnchor="middle" fill="hsl(0,50%,35%)" fontSize="8" fontFamily="JetBrains Mono, monospace">invisibile · non converte</text>
        </g>

        {/* CENTER arrow */}
        <g className="sg-cf-2">
          <circle cx="170" cy="100" r="14" fill="hsl(179,80%,36%,0.12)" stroke="hsl(179,80%,36%,0.25)" strokeWidth="1"/>
          <text x="170" y="105" textAnchor="middle" fill={TEAL2} fontSize="16" fontFamily="Space Grotesk, sans-serif">→</text>
        </g>

        {/* RIGHT , custom site */}
        <g className="sg-cf-3">
          <rect x="182" y="14" width="148" height="172" rx="9" fill={SURF} stroke="hsl(179,80%,36%,0.28)" strokeWidth="1"/>
          <rect x="182" y="14" width="148" height="24" rx="9" fill="hsl(179,80%,20%,0.3)"/>
          <rect x="182" y="26" width="148" height="12" fill="hsl(179,80%,20%,0.3)"/>
          <text x="256" y="31" textAnchor="middle" fill={TEAL} fontSize="8" fontFamily="JetBrains Mono, monospace" letterSpacing="1.5">SU MISURA D4</text>

          {/* metric 1 */}
          <text x="256" y="65" textAnchor="middle" fill={FG} fontSize="30" fontWeight="700" fontFamily="Space Grotesk, sans-serif">p.1-3</text>
          <text x="256" y="80" textAnchor="middle" fill={TEAL} fontSize="8" fontFamily="JetBrains Mono, monospace">posizione media Google</text>

          <line x1="198" y1="96" x2="314" y2="96" stroke="hsl(210,30%,11%)" strokeWidth="1"/>

          {/* metric 2 */}
          <text x="256" y="125" textAnchor="middle" fill={FG} fontSize="30" fontWeight="700" fontFamily="Space Grotesk, sans-serif">+8×</text>
          <text x="256" y="140" textAnchor="middle" fill={TEAL} fontSize="8" fontFamily="JetBrains Mono, monospace">contatti in più ogni mese</text>

          <line x1="198" y1="156" x2="314" y2="156" stroke="hsl(210,30%,11%)" strokeWidth="1"/>

          <text x="256" y="173" textAnchor="middle" fill="hsl(179,80%,36%)" fontSize="8" fontFamily="JetBrains Mono, monospace">visibile · converte · cresce</text>
        </g>
      </g>
    </svg>
  );
}

function CustomVsTemplate() {
  return (
    <svg viewBox="0 0 340 195" overflow="hidden" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto" aria-hidden="true">
      <defs>
        <clipPath id="sg-cvt-c"><rect width="340" height="195" rx="12"/></clipPath>
        <linearGradient id="sg-cvt-brand" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={TEAL2}/>
          <stop offset="100%" stopColor={TEAL}/>
        </linearGradient>
        <style>{`
          @keyframes sg-cvt-in{from{opacity:0}to{opacity:1}}
          .sg-cvt-1{animation:sg-cvt-in .5s ease-out .1s both}
          .sg-cvt-2{animation:sg-cvt-in .5s ease-out .4s both}
        `}</style>
      </defs>
      <g clipPath="url(#sg-cvt-c)">
        <rect width="340" height="195" rx="12" fill={BG}/>
        <line x1="170" y1="10" x2="170" y2="185" stroke="hsl(200,30%,94%,0.05)" strokeWidth="1"/>
        <rect x="153" y="86" width="34" height="22" rx="11" fill="hsl(210,30%,8%)" stroke={BORDER} strokeWidth="1"/>
        <text x="170" y="101" textAnchor="middle" fill={DIM} fontSize="8" fontFamily="JetBrains Mono, monospace">VS</text>

        {/* TEMPLATE side */}
        <g className="sg-cvt-1">
          <text x="85" y="24" textAnchor="middle" fill="hsl(0,50%,55%)" fontSize="8.5" fontFamily="JetBrains Mono, monospace" letterSpacing="1">TEMPLATE</text>
          <rect x="18" y="32" width="134" height="140" rx="7" fill="hsl(210,25%,8%)" stroke={BORDER} strokeWidth="1"/>
          {/* browser bar */}
          <rect x="18" y="32" width="134" height="16" rx="7" fill="hsl(210,25%,11%)"/>
          <rect x="18" y="40" width="134" height="8" fill="hsl(210,25%,11%)"/>
          {[26,34,42].map((x,i) => <circle key={i} cx={x} cy="40" r="2.5" fill="hsl(210,25%,16%)"/>)}
          {/* nav */}
          <rect x="18" y="48" width="134" height="18" fill="hsl(210,25%,9%)"/>
          {[60,80,100,120].map((x,i) => <rect key={i} x={x} y="54" width="14" height="5" rx="2" fill="hsl(210,25%,14%)"/>)}
          {/* hero */}
          <rect x="18" y="66" width="134" height="38" fill="hsl(210,25%,7%)"/>
          <rect x="28" y="74" width="70" height="8" rx="2" fill="hsl(210,25%,15%)"/>
          <rect x="28" y="86" width="44" height="6" rx="2" fill="hsl(210,25%,13%)"/>
          <rect x="28" y="97" width="34" height="6" rx="5" fill="hsl(210,25%,17%)"/>
          <rect x="82" y="70" width="60" height="30" rx="3" fill="hsl(210,25%,12%)"/>
          {/* content rows */}
          {[0,1,2].map(i => <rect key={i} x={24} y={110+i*18} width={118} height={12} rx="3" fill="hsl(210,25%,10%)"/>)}
          {/* footer */}
          <rect x="18" y="162" width="134" height="10" fill="hsl(210,25%,8%)"/>
          {/* label */}
          <text x="85" y="184" textAnchor="middle" fill="hsl(0,50%,40%)" fontSize="8" fontFamily="JetBrains Mono, monospace">uguale a migliaia di siti</text>
        </g>

        {/* CUSTOM side */}
        <g className="sg-cvt-2">
          <text x="255" y="24" textAnchor="middle" fill={TEAL} fontSize="8.5" fontFamily="JetBrains Mono, monospace" letterSpacing="1">SU MISURA</text>
          <rect x="188" y="32" width="134" height="140" rx="7" fill="hsl(216,45%,5%)" stroke="hsl(179,80%,36%,0.28)" strokeWidth="1"/>
          {/* browser bar */}
          <rect x="188" y="32" width="134" height="16" rx="7" fill="hsl(210,30%,9%)"/>
          <rect x="188" y="40" width="134" height="8" fill="hsl(210,30%,9%)"/>
          {[196,204,212].map((x,i) => <circle key={i} cx={x} cy="40" r="2.5" fill={i===0?"hsl(0,60%,55%)":i===1?"hsl(40,80%,55%)":"hsl(120,50%,45%)"}  opacity=".6"/>)}
          {/* nav branded */}
          <rect x="188" y="48" width="134" height="18" fill="hsl(210,30%,7%)"/>
          <rect x="196" y="52" width="10" height="10" rx="2" fill="url(#sg-cvt-brand)"/>
          <rect x="212" y="55" width="28" height="5" rx="2" fill={FG} opacity=".5"/>
          <rect x="296" y="51" width="20" height="12" rx="6" fill="url(#sg-cvt-brand)"/>
          {/* hero */}
          <rect x="188" y="66" width="134" height="38" fill="hsl(216,45%,4%)"/>
          <rect x="198" y="74" width="68" height="9" rx="2" fill={FG} opacity=".85"/>
          <rect x="198" y="88" width="48" height="6" rx="2" fill="url(#sg-cvt-brand)" opacity=".65"/>
          <rect x="198" y="97" width="36" height="6" rx="5" fill="url(#sg-cvt-brand)"/>
          <rect x="272" y="70" width="44" height="30" rx="3" fill="hsl(179,60%,12%)" opacity=".8"/>
          {/* content rows */}
          {[0,1,2].map(i => <rect key={i} x={194} y={110+i*18} width={118} height={12} rx="3" fill="hsl(210,30%,8%)" stroke="hsl(179,80%,36%,0.08)" strokeWidth="1"/>)}
          {/* footer */}
          <rect x="188" y="162" width="134" height="10" fill="hsl(210,30%,7%)"/>
          {/* label */}
          <text x="255" y="184" textAnchor="middle" fill={TEAL2} fontSize="8" fontFamily="JetBrains Mono, monospace">identità unica · costruita per te</text>
        </g>
      </g>
    </svg>
  );
}

function WorkflowSteps() {
  const steps = [
    { x: 34,  n: "1", label: "Analisi",   sub: "brief + competitor" },
    { x: 102, n: "2", label: "Design",    sub: "mockup approvato" },
    { x: 170, n: "3", label: "Sviluppo",  sub: "WordPress su misura" },
    { x: 238, n: "4", label: "Test",      sub: "mobile · speed · SEO" },
    { x: 306, n: "5", label: "Lancio",    sub: "live in 14 giorni", accent: true },
  ];
  return (
    <svg viewBox="0 0 340 130" overflow="hidden" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto" aria-hidden="true">
      <defs>
        <clipPath id="sg-ws-c"><rect width="340" height="130" rx="12"/></clipPath>
        <linearGradient id="sg-ws-brand" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={TEAL2}/>
          <stop offset="100%" stopColor={TEAL}/>
        </linearGradient>
        <style>{`
          @keyframes sg-ws-draw{from{stroke-dashoffset:300}to{stroke-dashoffset:0}}
          @keyframes sg-ws-up{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}
          .sg-ws-line{stroke-dasharray:300;animation:sg-ws-draw 1.2s ease-out .3s both}
          .sg-ws-s1{animation:sg-ws-up .3s ease-out .15s both}
          .sg-ws-s2{animation:sg-ws-up .3s ease-out .35s both}
          .sg-ws-s3{animation:sg-ws-up .3s ease-out .55s both}
          .sg-ws-s4{animation:sg-ws-up .3s ease-out .75s both}
          .sg-ws-s5{animation:sg-ws-up .3s ease-out .95s both}
        `}</style>
      </defs>
      <g clipPath="url(#sg-ws-c)">
        <rect width="340" height="130" rx="12" fill={BG}/>
        {/* track */}
        <line x1="34" y1="54" x2="306" y2="54" stroke="hsl(210,30%,13%)" strokeWidth="2" strokeLinecap="round"/>
        <line x1="34" y1="54" x2="306" y2="54" stroke="url(#sg-ws-brand)" strokeWidth="2" strokeLinecap="round" className="sg-ws-line"/>
        {steps.map(({ x, n, label, sub, accent }, i) => (
          <g key={x} className={`sg-ws-s${i+1}`}>
            {/* outer ring */}
            {accent && <circle cx={x} cy="54" r="19" fill="hsl(179,80%,36%,0.1)" stroke="hsl(179,80%,36%,0.35)" strokeWidth="1"/>}
            <circle cx={x} cy="54" r={accent ? 14 : 12}
              fill={accent ? "url(#sg-ws-brand)" : "hsl(210,30%,8%)"}
              stroke={accent ? "none" : "url(#sg-ws-brand)"} strokeWidth="1.5"/>
            <text x={x} y={accent ? 59 : 58} textAnchor="middle"
              fill={accent ? "hsl(216,45%,3%)" : TEAL}
              fontSize={accent ? "12" : "10"} fontWeight="700" fontFamily="Space Grotesk, sans-serif">{n}</text>
            <text x={x} y="84" textAnchor="middle"
              fill={accent ? TEAL : FG}
              fontSize="9" fontWeight="600" fontFamily="Space Grotesk, sans-serif">{label}</text>
            <text x={x} y="98" textAnchor="middle"
              fill={DIM} fontSize="7.5" fontFamily="JetBrains Mono, monospace">{sub}</text>
          </g>
        ))}
        <text x="170" y="118" textAnchor="middle" fill={DIM2} fontSize="7.5" fontFamily="JetBrains Mono, monospace" letterSpacing="1">CONSEGNA GARANTITA IN 14 GIORNI LAVORATIVI</text>
      </g>
    </svg>
  );
}

function TargetClients() {
  const cards = [
    {
      x: 12, label: "Professionisti", sub1: "Avvocati · Commerc.", sub2: "Architetti · Medici",
      Icon: IconBriefcase, cls: "sg-tc-1",
    },
    {
      x: 120, label: "PMI & Artigiani", sub1: "Officine · Ristoranti", sub2: "Negozi · Studi",
      Icon: IconWrench, cls: "sg-tc-2",
    },
    {
      x: 228, label: "In crescita", sub1: "Sito vecchio da rifare", sub2: "Vuoi più contatti",
      Icon: IconTrend, cls: "sg-tc-3",
    },
  ];
  return (
    <svg viewBox="0 0 340 148" overflow="hidden" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto" aria-hidden="true">
      <defs>
        <clipPath id="sg-tc-c"><rect width="340" height="148" rx="12"/></clipPath>
        <linearGradient id="sg-tc-brand" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={TEAL2}/>
          <stop offset="100%" stopColor={TEAL}/>
        </linearGradient>
        <style>{`
          @keyframes sg-tc-up{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
          .sg-tc-1{animation:sg-tc-up .4s ease-out .1s both}
          .sg-tc-2{animation:sg-tc-up .4s ease-out .25s both}
          .sg-tc-3{animation:sg-tc-up .4s ease-out .4s both}
        `}</style>
      </defs>
      <g clipPath="url(#sg-tc-c)">
        <rect width="340" height="148" rx="12" fill={BG}/>
        {cards.map(({ x, label, sub1, sub2, Icon, cls }) => (
          <g key={x} className={cls}>
            <rect x={x} y="12" width="100" height="124" rx="9"
              fill={SURF} stroke="hsl(179,80%,36%,0.18)" strokeWidth="1"/>
            {/* icon area */}
            <circle cx={x+50} cy="44" r="18" fill="hsl(179,80%,36%,0.1)" stroke="hsl(179,80%,36%,0.2)" strokeWidth="1"/>
            <g transform={`translate(${x+50-8},${44-8})`}>
              <Icon color={TEAL}/>
            </g>
            {/* label */}
            <text x={x+50} y="77" textAnchor="middle" fill={FG}
              fontSize="9" fontWeight="700" fontFamily="Space Grotesk, sans-serif"
              textLength="88" lengthAdjust="spacingAndGlyphs">{label}</text>
            {/* sub lines */}
            <text x={x+50} y="93" textAnchor="middle" fill={DIM}
              fontSize="7.5" fontFamily="JetBrains Mono, monospace"
              textLength="86" lengthAdjust="spacingAndGlyphs">{sub1}</text>
            <text x={x+50} y="107" textAnchor="middle" fill={DIM2}
              fontSize="7.5" fontFamily="JetBrains Mono, monospace"
              textLength="86" lengthAdjust="spacingAndGlyphs">{sub2}</text>
            {/* cta pill */}
            <rect x={x+20} y="118" width="60" height="12" rx="6" fill="url(#sg-tc-brand)" opacity=".15"/>
            <text x={x+50} y="128" textAnchor="middle" fill={TEAL}
              fontSize="7" fontFamily="JetBrains Mono, monospace">WordPress su misura</text>
          </g>
        ))}
      </g>
    </svg>
  );
}

function Guarantees() {
  const items = [
    { x: 8,   label: "30gg",        sub: "assistenza",   Icon: IconShield   },
    { x: 92,  label: "Codice",      sub: "tutto tuo",    Icon: IconCode     },
    { x: 176, label: "No",          sub: "lock-in",      Icon: IconUnlock   },
    { x: 260, label: "Scadenze",    sub: "rispettate",   Icon: IconCalendar },
  ];
  return (
    <svg viewBox="0 0 340 106" overflow="hidden" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto" aria-hidden="true">
      <defs>
        <clipPath id="sg-gua-c"><rect width="340" height="106" rx="12"/></clipPath>
        <style>{`
          @keyframes sg-gua-up{from{opacity:0;transform:translateY(7px)}to{opacity:1;transform:translateY(0)}}
          .sg-g1{animation:sg-gua-up .3s ease-out .05s both}
          .sg-g2{animation:sg-gua-up .3s ease-out .15s both}
          .sg-g3{animation:sg-gua-up .3s ease-out .25s both}
          .sg-g4{animation:sg-gua-up .3s ease-out .35s both}
        `}</style>
      </defs>
      <g clipPath="url(#sg-gua-c)">
        <rect width="340" height="106" rx="12" fill={BG}/>
        {items.map(({ x, label, sub, Icon }, i) => (
          <g key={x} className={`sg-g${i+1}`}>
            <rect x={x} y="10" width="72" height="86" rx="8"
              fill={SURF} stroke="hsl(179,80%,36%,0.2)" strokeWidth="1"/>
            {/* icon */}
            <circle cx={x+36} cy="38" r="14" fill="hsl(179,80%,36%,0.12)"/>
            <g transform={`translate(${x+36-8},${38-8})`}>
              <Icon color={TEAL}/>
            </g>
            {/* labels */}
            <text x={x+36} y="65" textAnchor="middle" fill={TEAL}
              fontSize="9" fontWeight="700" fontFamily="Space Grotesk, sans-serif"
              textLength="58" lengthAdjust="spacingAndGlyphs">{label}</text>
            <text x={x+36} y="78" textAnchor="middle" fill={DIM}
              fontSize="8" fontFamily="JetBrains Mono, monospace"
              textLength="58" lengthAdjust="spacingAndGlyphs">{sub}</text>
          </g>
        ))}
      </g>
    </svg>
  );
}

function PerfCost() {
  // Value label goes ABOVE the row now (not inside the bar) so there is no
  // overflow on narrow viewports. Track is narrower, labels wider + larger.
  // Sources are multi-attributed for credibility.
  const rows = [
    {
      label: "Carica in 3 secondi",
      value: "53% degli utenti esce",
      barPct: 0.9,
      tone: "bad" as const,
    },
    {
      label: "Carica in 2 secondi",
      value: "38% degli utenti esce",
      barPct: 0.65,
      tone: "warn" as const,
    },
    {
      label: "Carica in 1.2 secondi",
      value: "+20% di conversioni",
      barPct: 0.78,
      tone: "good" as const,
    },
  ];

  return (
    <svg
      viewBox="0 0 360 210"
      overflow="hidden"
      xmlns="http://www.w3.org/2000/svg"
      className="w-full h-auto"
      aria-hidden="true"
    >
      <defs>
        <clipPath id="sg-pc-c">
          <rect width="360" height="210" rx="14" />
        </clipPath>
        <linearGradient id="sg-pc-good" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={TEAL2} />
          <stop offset="100%" stopColor={TEAL} />
        </linearGradient>
        <linearGradient id="sg-pc-warn" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#f59e0b" />
          <stop offset="100%" stopColor="#fbbf24" />
        </linearGradient>
        <linearGradient id="sg-pc-bad" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#ef4444" />
          <stop offset="100%" stopColor="#f97316" />
        </linearGradient>
        <style>{`
          @keyframes sg-pc-bar{from{width:4px}to{width:var(--w)}}
          .sg-pc-b{animation:sg-pc-bar .9s cubic-bezier(.4,0,.2,1) .4s both;width:var(--w)}
        `}</style>
      </defs>

      <g clipPath="url(#sg-pc-c)">
        <rect width="360" height="210" rx="14" fill={BG} />

        {/* Title */}
        <text
          x="20"
          y="26"
          fill={TEAL}
          fontSize="10"
          fontFamily="JetBrains Mono, monospace"
          letterSpacing="1.5"
          fontWeight="600"
        >
          VELOCITÀ E COMPORTAMENTO UTENTE
        </text>
        <text
          x="20"
          y="42"
          fill={DIM}
          fontSize="9"
          fontFamily="Inter, sans-serif"
        >
          Cosa succede sul sito al variare del tempo di caricamento
        </text>

        {/* Rows: each occupies a taller block, label above bar */}
        {rows.map((r, i) => {
          const blockY = 58 + i * 46;
          const gradId =
            r.tone === "good" ? "sg-pc-good" : r.tone === "warn" ? "sg-pc-warn" : "sg-pc-bad";
          const textColor =
            r.tone === "good" ? TEAL : r.tone === "warn" ? "#fbbf24" : "#f97316";
          const trackW = 320;
          return (
            <g key={i}>
              {/* Above the bar: left = label, right = value */}
              <text
                x="20"
                y={blockY}
                fill={FG}
                fontSize="10"
                fontFamily="Inter, sans-serif"
                fontWeight="500"
              >
                {r.label}
              </text>
              <text
                x="340"
                y={blockY}
                textAnchor="end"
                fill={textColor}
                fontSize="10"
                fontFamily="Inter, sans-serif"
                fontWeight="600"
              >
                {r.value}
              </text>
              {/* Track */}
              <rect
                x="20"
                y={blockY + 8}
                width={trackW}
                height="10"
                rx="5"
                fill="hsl(210,30%,10%)"
              />
              {/* Bar */}
              <rect
                x="20"
                y={blockY + 8}
                height="10"
                rx="5"
                fill={`url(#${gradId})`}
                style={{ "--w": `${Math.round(trackW * r.barPct)}px` } as React.CSSProperties}
                className="sg-pc-b"
              />
            </g>
          );
        })}

        {/* Sources , multi-attributed */}
        <text
          x="20"
          y="198"
          fill={DIM2}
          fontSize="7.5"
          fontFamily="JetBrains Mono, monospace"
        >
          fonti: Google Chrome UX Report · Think with Google · Portent 2022
        </text>
      </g>
    </svg>
  );
}

function AuditCycle() {
  return (
    <svg viewBox="0 0 340 130" overflow="hidden" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto" aria-hidden="true">
      <defs>
        <clipPath id="sg-ac-c"><rect width="340" height="130" rx="12"/></clipPath>
        <linearGradient id="sg-ac-brand" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={TEAL2}/>
          <stop offset="100%" stopColor={TEAL}/>
        </linearGradient>
        <style>{`
          @keyframes sg-ac-in{from{opacity:0}to{opacity:1}}
          .sg-a1{animation:sg-ac-in .3s ease-out .1s both}
          .sg-a2{animation:sg-ac-in .3s ease-out .3s both}
          .sg-a3{animation:sg-ac-in .3s ease-out .5s both}
          .sg-a4{animation:sg-ac-in .3s ease-out .7s both}
        `}</style>
      </defs>
      <g clipPath="url(#sg-ac-c)">
        <rect width="340" height="130" rx="12" fill={BG}/>
        {[100,178,256].map(x => (
          <text key={x} x={x} y="66" textAnchor="middle" fill={TEAL2} fontSize="16">→</text>
        ))}
        {[
          { x: 22,  n: "1", label: "Audit",       sub1: "Lighthouse",   sub2: "+ GTmetrix",  cls: "sg-a1" },
          { x: 112, n: "2", label: "Priorità",    sub1: "impatto",      sub2: "vs effort",   cls: "sg-a2" },
          { x: 202, n: "3", label: "Intervento",  sub1: "cache · img",  sub2: "CSS / JS",    cls: "sg-a3" },
          { x: 292, n: "4", label: "Misurazione", sub1: "before/after", sub2: "CrUX data",   cls: "sg-a4", accent: true },
        ].map(({ x, n, label, sub1, sub2, cls, accent }) => (
          <g key={x} className={cls}>
            <rect x={x} y="16" width="76" height="96" rx="8"
              fill={accent ? "hsl(179,80%,36%,0.08)" : SURF}
              stroke={accent ? "hsl(179,80%,36%,0.35)" : BORDER} strokeWidth="1"/>
            <circle cx={x+38} cy="40" r="13"
              fill={accent ? "url(#sg-ac-brand)" : "hsl(179,80%,36%,0.15)"}/>
            <text x={x+38} y="45" textAnchor="middle"
              fill={accent ? "hsl(216,45%,3%)" : TEAL}
              fontSize="12" fontWeight="700">{n}</text>
            <text x={x+38} y="68" textAnchor="middle"
              fill={accent ? TEAL : FG}
              fontSize="8.5" fontWeight="600" fontFamily="Space Grotesk, sans-serif"
              textLength="66" lengthAdjust="spacingAndGlyphs">{label}</text>
            {[sub1, sub2].map((line, i) => (
              <text key={i} x={x+38} y={83+i*12} textAnchor="middle"
                fill={DIM2} fontSize="7.5" fontFamily="JetBrains Mono, monospace"
                textLength="64" lengthAdjust="spacingAndGlyphs">{line}</text>
            ))}
          </g>
        ))}
      </g>
    </svg>
  );
}

function PerfSkills() {
  return (
    <svg viewBox="0 0 340 148" overflow="hidden" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto" aria-hidden="true">
      <defs>
        <clipPath id="sg-ps-c"><rect width="340" height="148" rx="12"/></clipPath>
        <linearGradient id="sg-ps-brand" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={TEAL2}/>
          <stop offset="100%" stopColor={TEAL}/>
        </linearGradient>
        <style>{`
          @keyframes sg-ps-up{from{opacity:0;transform:translateY(7px)}to{opacity:1;transform:translateY(0)}}
          .sg-ps-1{animation:sg-ps-up .3s ease-out .05s both}.sg-ps-2{animation:sg-ps-up .3s ease-out .15s both}
          .sg-ps-3{animation:sg-ps-up .3s ease-out .25s both}.sg-ps-4{animation:sg-ps-up .3s ease-out .35s both}
          .sg-ps-r1{animation:sg-ps-up .3s ease-out .55s both}.sg-ps-r2{animation:sg-ps-up .3s ease-out .65s both}
          .sg-ps-r3{animation:sg-ps-up .3s ease-out .75s both}.sg-ps-r4{animation:sg-ps-up .3s ease-out .85s both}
        `}</style>
      </defs>
      <g clipPath="url(#sg-ps-c)">
        <rect width="340" height="148" rx="12" fill={BG}/>
        <text x="170" y="18" textAnchor="middle" fill={DIM2} fontSize="8" fontFamily="JetBrains Mono, monospace" letterSpacing="1">STRUMENTI DI ANALISI</text>
        {[
          { x: 8,   label: "Lighthouse",   color: "hsl(25,80%,60%)",  cls: "sg-ps-1" },
          { x: 92,  label: "GTmetrix",     color: "hsl(200,80%,60%)", cls: "sg-ps-2" },
          { x: 176, label: "DevTools",     color: "hsl(260,65%,70%)", cls: "sg-ps-3" },
          { x: 260, label: "WebPageTest",  color: TEAL,               cls: "sg-ps-4" },
        ].map(({ x, label, color, cls }) => (
          <g key={label} className={cls}>
            <rect x={x} y="24" width="74" height="28" rx="7" fill={SURF} stroke={color} strokeWidth="0.8" opacity="0.8"/>
            <text x={x+37} y="42" textAnchor="middle" fill={color} fontSize="8.5" fontFamily="JetBrains Mono, monospace"
              textLength="62" lengthAdjust="spacingAndGlyphs">{label}</text>
          </g>
        ))}
        <text x="170" y="75" textAnchor="middle" fill={TEAL2} fontSize="12">↓</text>
        <text x="170" y="90" textAnchor="middle" fill={DIM2} fontSize="8" fontFamily="JetBrains Mono, monospace" letterSpacing="1">RISULTATI TIPICI</text>
        {[
          { x: 8,   label: "LCP −60%",     cls: "sg-ps-r1" },
          { x: 92,  label: "+50 punti PSI", cls: "sg-ps-r2" },
          { x: 176, label: "Bounce −25%",  cls: "sg-ps-r3" },
          { x: 260, label: "Report B/A",   cls: "sg-ps-r4" },
        ].map(({ x, label, cls }) => (
          <g key={label} className={cls}>
            <rect x={x} y="96" width="74" height="28" rx="7" fill="hsl(179,80%,36%,0.09)" stroke="hsl(179,80%,36%,0.3)" strokeWidth="0.8"/>
            <text x={x+37} y="114" textAnchor="middle" fill={TEAL} fontSize="8.5" fontFamily="JetBrains Mono, monospace"
              textLength="62" lengthAdjust="spacingAndGlyphs">{label}</text>
          </g>
        ))}
        <text x="170" y="140" textAnchor="middle" fill="hsl(205,18%,28%)" fontSize="7.5" fontFamily="JetBrains Mono, monospace"
          textLength="300" lengthAdjust="spacingAndGlyphs">OGNI INTERVENTO È MISURABILE · DATI BEFORE / AFTER</text>
      </g>
    </svg>
  );
}

function AboutCraft() {
  // "5 skill in input → risultati concreti in output" , for WP creation landing
  const skills  = ["WordPress", "SEO tecnico", "Performance", "UX / Design", "Supporto"];
  const outputs = ["Veloce su mobile", "Google p.1-3", "Più contatti", "Gestione autonoma", "30gg assistenza"];
  return (
    <svg viewBox="0 0 340 130" overflow="hidden" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto" aria-hidden="true">
      <defs>
        <clipPath id="sg-ab-c"><rect width="340" height="130" rx="12"/></clipPath>
        <linearGradient id="sg-ab-brand" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={TEAL2}/>
          <stop offset="100%" stopColor={TEAL}/>
        </linearGradient>
        <style>{`
          @keyframes sg-ab-up{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}
          @keyframes sg-ab-line{from{opacity:0}to{opacity:1}}
          .sg-ab-in{animation:sg-ab-up .4s ease-out .1s both}
          .sg-ab-out{animation:sg-ab-up .4s ease-out .5s both}
          .sg-ab-c{animation:sg-ab-up .4s ease-out .3s both}
          .sg-ab-l{animation:sg-ab-line .3s ease-out .6s both}
        `}</style>
      </defs>
      <g clipPath="url(#sg-ab-c)">
        <rect width="340" height="130" rx="12" fill={BG}/>

        {/* LEFT , skills panel */}
        <g className="sg-ab-in">
          <rect x="8" y="12" width="138" height="106" rx="8" fill={SURF} stroke={BORDER} strokeWidth="1"/>
          <text x="77" y="28" textAnchor="middle" fill={DIM} fontSize="7.5" fontFamily="JetBrains Mono, monospace" letterSpacing="1">COSA PORTO</text>
          {skills.map((s, i) => (
            <g key={i}>
              <circle cx="24" cy={43+i*17} r="2.5" fill={TEAL2} opacity=".8"/>
              <text x="33" y={47+i*17} fill={FG} fontSize="9" fontFamily="Space Grotesk, sans-serif">{s}</text>
            </g>
          ))}
        </g>

        {/* CENTER , D4 badge */}
        <g className="sg-ab-c">
          <circle cx="170" cy="65" r="22" fill="hsl(179,80%,36%,0.1)" stroke="hsl(179,80%,36%,0.3)" strokeWidth="1.5"/>
          <circle cx="170" cy="65" r="14" fill="url(#sg-ab-brand)"/>
          <text x="170" y="70" textAnchor="middle" fill="hsl(216,45%,3%)" fontSize="12" fontWeight="800" fontFamily="Space Grotesk, sans-serif">D4</text>
        </g>

        {/* arrow */}
        <g className="sg-ab-l">
          <line x1="148" y1="65" x2="157" y2="65" stroke={TEAL2} strokeWidth="1.5" strokeLinecap="round"/>
          <line x1="183" y1="65" x2="194" y2="65" stroke={TEAL2} strokeWidth="1.5" strokeLinecap="round"/>
        </g>

        {/* RIGHT , outputs panel */}
        <g className="sg-ab-out">
          <rect x="194" y="12" width="138" height="106" rx="8" fill="hsl(216,45%,4%)" stroke="hsl(179,80%,36%,0.22)" strokeWidth="1"/>
          <text x="263" y="28" textAnchor="middle" fill={TEAL2} fontSize="7.5" fontFamily="JetBrains Mono, monospace" letterSpacing="1">COSA OTTIENI</text>
          {outputs.map((s, i) => (
            <g key={i}>
              {/* check mark via path */}
              <g transform={`translate(${210},${37+i*17})`}>
                <polyline points="0,3 2,5 6,0" fill="none" stroke={TEAL} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
              </g>
              <text x="222" y={47+i*17} fill={FG} fontSize="9" fontFamily="Space Grotesk, sans-serif">{s}</text>
            </g>
          ))}
        </g>
      </g>
    </svg>
  );
}

// ─── registry ────────────────────────────────────────────────────────────────

const GRAPHICS: Record<string, React.ComponentType> = {
  "conversion-funnel":  ConversionFunnel,
  "custom-vs-template": CustomVsTemplate,
  "workflow-5steps":    WorkflowSteps,
  "target-clients":     TargetClients,
  "guarantees":         Guarantees,
  "perf-cost":          PerfCost,
  "audit-cycle":        AuditCycle,
  "perf-skills":        PerfSkills,
  "about-craft":        AboutCraft,
};

interface SectionGraphicProps { id: string }

export function SectionGraphic({ id }: SectionGraphicProps) {
  const Graphic = GRAPHICS[id];
  if (!Graphic) return null;
  return (
    <div className="not-prose my-8 rounded-xl overflow-hidden border border-[hsl(var(--border))] shadow-[0_4px_24px_hsl(179,80%,36%,0.06)]">
      <Graphic />
    </div>
  );
}
